import { MeshProps } from '@react-three/fiber';
import { useRef, useMemo } from 'react';
import * as THREE from 'three';
import PCNode from '../Nodes/PCNode';
import * as PCConstants from '../PCConstants';
import { Model } from '../../hooks/useModelState';
import { cadToThreeJsPositionWithOffset } from '../../Scenes/scene-utils';

const getStopeMeshColour = (node: PCNode, isHovered: boolean): string => {
  if (isHovered) return PCConstants.HOVER_COLOUR;
  const hasOverride = node.nodeColour
    && node.nodeColour !== PCConstants.NODE_COLOUR
    && node.nodeColour !== PCConstants.STOPE_COLOUR;
  return hasOverride ? node.nodeColour : PCConstants.STOPE_COLOUR;
};

interface NodeMeshProps extends MeshProps {
  node: PCNode;
  stopeId?: string;
  isSelected: boolean;
  isHovered: boolean;
  onHover: (node: PCNode | null) => void;
  onSelect: (node: PCNode, stopeId?: string) => void;
  onFocus: (node: PCNode, worldPosition?: THREE.Vector3) => void;
  model: Model;
  offset: THREE.Vector3;
  scale: number;
}

const StopeMesh = ({
  node,
  stopeId,
  isHovered,
  onHover,
  onSelect,
  onFocus,
  offset,
  scale,
  ...props
}: NodeMeshProps) => {
  const normalizedPosition = useMemo(
    (): [number, number, number] => cadToThreeJsPositionWithOffset(node.coords, offset, scale),
    [node.coords, offset, scale],
  );

  const clickTimeoutRef = useRef<number | null>(null);
  const doubleClickRef = useRef(false);

  const handleClick = (e: any) => {
    e.stopPropagation();
    doubleClickRef.current = false;
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }
    clickTimeoutRef.current = window.setTimeout(() => {
      if (!doubleClickRef.current) {
        onSelect(node, stopeId);
      }
      clickTimeoutRef.current = null;
    }, 200);
  };

  const handleDoubleClick = (e: any) => {
    e.stopPropagation();
    doubleClickRef.current = true;
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }
    const [x, y, z] = cadToThreeJsPositionWithOffset(node.coords, offset, scale);
    const worldPosition = new THREE.Vector3(x, y, z);
    onFocus(node, worldPosition);
  };

  return (
    <>
      <mesh
        {...props}
        position={normalizedPosition}
        onPointerOver={(e) => { e.stopPropagation(); if (e.nativeEvent?.buttons) return; onHover(node); }}
        onPointerOut={(e) => {
          e.stopPropagation();
          if (e.nativeEvent?.buttons) return;
          onHover(null);
        }}
        userData={{ nodeId: node.id }}
      >
        <sphereGeometry args={[PCConstants.STOPE_RADIUS * scale, 20, 20]} />
        <meshBasicMaterial color={getStopeMeshColour(node, isHovered)} transparent={false} />
      </mesh>
      {/* Invisible larger mesh for easier double-click targeting - provides 2x hitbox radius while keeping visual appearance unchanged */}
      <mesh
        {...props}
        position={normalizedPosition}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        visible={false}
        userData={{ nodeId: node.id, doubleClickHitbox: true }}
      >
        <sphereGeometry args={[PCConstants.STOPE_RADIUS * 2 * scale, 20, 20]} />
        <meshBasicMaterial transparent opacity={0} />
      </mesh>
    </>
  );
};

export default StopeMesh;
